import React, { useState, useEffect } from 'react'
import { useAuth } from '../domains/auth/AuthContext'
import { authApi } from '../services/api'
import toast from 'react-hot-toast'
import { 
  BuildingOfficeIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowRightIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'

interface Organization {
  id: string
  name: string
  logoUrl?: string
  role: string
  status: string
  isCurrent?: boolean
}

export default function Organizations() {
  const { user, selectOrganization } = useAuth()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [switching, setSwitching] = useState<string | null>(null)

  useEffect(() => {
    loadOrganizations()
  }, [])

  const loadOrganizations = async () => {
    try {
      setLoading(true)
      // TODO: Replace with actual API call when organizations endpoint is available
      // For now, we'll use mock data
      const mockOrganizations: Organization[] = [
        {
          id: '1',
          name: 'Acme Corporation',
          logoUrl: 'https://via.placeholder.com/50x50?text=AC',
          role: 'EMPLOYEE',
          status: 'ACCEPTED',
          isCurrent: true
        },
        {
          id: '2',
          name: 'Tech Solutions Inc',
          logoUrl: 'https://via.placeholder.com/50x50?text=TS',
          role: 'ADMIN',
          status: 'ACCEPTED',
          isCurrent: false
        },
        {
          id: '3',
          name: 'Security Plus',
          logoUrl: 'https://via.placeholder.com/50x50?text=SP',
          role: 'EMPLOYEE',
          status: 'PENDING',
          isCurrent: false
        }
      ]
      
      setOrganizations(mockOrganizations)
    } catch (error) {
      console.error('Error loading organizations:', error)
      toast.error('Failed to load organizations')
    } finally {
      setLoading(false)
    }
  }

  const handleSwitchOrganization = async (organizationId: string) => {
    try {
      setSwitching(organizationId)
      
      // Use the AuthContext selectOrganization method
      await selectOrganization(organizationId)
      
      // Update the local state to reflect the change
      setOrganizations(prev => prev.map(org => ({
        ...org,
        isCurrent: org.id === organizationId
      })))
      
      // Reload organizations to get updated data
      await loadOrganizations()
      
    } catch (error) {
      console.error('Error switching organization:', error)
      toast.error('Failed to switch organization')
    } finally {
      setSwitching(null)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACCEPTED':
        return <CheckCircleIcon className="w-4 h-4 text-green-500" />
      case 'PENDING':
        return <ClockIcon className="w-4 h-4 text-yellow-500" />
      default:
        return <ClockIcon className="w-4 h-4 text-gray-500" />
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'ACCEPTED':
        return 'Active'
      case 'PENDING':
        return 'Pending'
      default:
        return status
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACCEPTED':
        return 'text-green-600 bg-green-100'
      case 'PENDING':
        return 'text-yellow-600 bg-yellow-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary mb-2">Organizations</h1>
        <p className="text-text-secondary">Manage your organization memberships</p>
      </div>

      {organizations.length === 0 ? (
        <div className="text-center py-8">
          <BuildingOfficeIcon className="w-16 h-16 mx-auto mb-4 text-text-muted" />
          <p className="text-text-secondary">No organizations found</p>
          <p className="text-sm text-text-muted mt-2">
            You'll see organizations here once you're invited to join them
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {organizations.map((org) => (
            <div key={org.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center space-x-4">
                {/* Organization Logo */}
                <div className="flex-shrink-0">
                  {org.logoUrl ? (
                    <img 
                      src={org.logoUrl} 
                      alt={org.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-primary-orange rounded-lg flex items-center justify-center">
                      <BuildingOfficeIcon className="w-6 h-6 text-white" />
                    </div>
                  )}
                </div>

                {/* Organization Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="font-semibold text-text-primary truncate">{org.name}</h3>
                    {org.isCurrent && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-orange text-white">
                        Current
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2 mb-2">
                    <UserGroupIcon className="w-4 h-4 text-text-muted" />
                    <span className="text-sm text-text-secondary capitalize">{org.role.toLowerCase()}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(org.status)}
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(org.status)}`}>
                      {getStatusText(org.status)}
                    </span>
                  </div>
                </div>

                {/* Action Button */}
                <div className="flex-shrink-0">
                  {org.status === 'ACCEPTED' && !org.isCurrent ? (
                    <button
                      onClick={() => handleSwitchOrganization(org.id)}
                      disabled={switching === org.id}
                      className="flex items-center space-x-1 text-primary-orange hover:text-primary-orange-dark disabled:opacity-50"
                    >
                      {switching === org.id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-orange"></div>
                          <span className="text-sm">Switching...</span>
                        </>
                      ) : (
                        <>
                          <span className="text-sm">Switch</span>
                          <ArrowRightIcon className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  ) : org.status === 'PENDING' ? (
                    <span className="text-sm text-text-muted">Waiting for approval</span>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Help Section */}
      <div className="mt-8 bg-blue-50 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">Need to join another organization?</h3>
        <p className="text-sm text-blue-700 mb-3">
          Contact your organization administrator to receive an invitation. You'll be able to switch between organizations once you're a member.
        </p>
        <div className="text-xs text-blue-600">
          <p>• Each organization has its own locations and reports</p>
          <p>• You can have different roles in different organizations</p>
          <p>• Switch between organizations to access different features</p>
        </div>
      </div>
    </div>
  )
} 